/* tslint:disable:max-classes-per-file */
interface IMaybe<T> {
  map<U>(fn: (value: T) => U): IMaybe<U>;
  chain<U>(fn: (value: T) => IMaybe<U>): IMaybe<U>;
  filter(fn: (value: T) => boolean): IMaybe<T>;
  find<U>(fn: (value: T) => U | undefined | null): IMaybe<U>;
  eitherOr<U>(defaultValue: U): T | U;
  unwrap(): T | undefined;
  unwrapOr<U>(defaultValue: U): T | U;
  getOrThrow(message?: string): T;
  isJust(): boolean;
  isNothing(): boolean;
}

class Just<T> implements IMaybe<T> {
  constructor(private value: T) {}

  public map<U>(fn: (value: T) => U): IMaybe<U> {
    return Maybe.of(fn(this.value));
  }

  public chain<U>(fn: (value: T) => IMaybe<U>): IMaybe<U> {
    return fn(this.value);
  }

  public filter(fn: (value: T) => boolean): IMaybe<T> {
    return fn(this.value) ? this : Maybe.nothing<T>();
  }

  public find<U>(fn: (value: T) => U | undefined | null): IMaybe<U> {
    const result = fn(this.value);
    return result == null ? Maybe.nothing<U>() : Maybe.of(result);
  }

  public eitherOr<U>(_: U): T {
    return this.value;
  }

  public unwrap(): T {
    return this.value;
  }

  public unwrapOr<U>(_: U): T {
    return this.value;
  }

  public getOrThrow(): T {
    return this.value;
  }

  public isJust() {
    return true;
  }

  public isNothing() {
    return false;
  }
}

class Nothing<T> implements IMaybe<T> {
  public map<U>(): IMaybe<U> {
    return Maybe.nothing<U>();
  }

  public chain<U>(): IMaybe<U> {
    return Maybe.nothing<U>();
  }

  public filter(): IMaybe<T> {
    return this;
  }

  public find<U>(): IMaybe<U> {
    return Maybe.nothing<U>();
  }

  public eitherOr<U>(defaultValue: U): U {
    return defaultValue;
  }

  public unwrap(): undefined {
    return undefined;
  }

  public unwrapOr<U>(defaultValue: U): U {
    return defaultValue;
  }

  public getOrThrow(message = 'Tried to get value from Nothing'): T {
    throw new Error(message);
  }

  public isJust() {
    return false;
  }

  public isNothing() {
    return true;
  }
}

/**
 * Maybe monad for safe handling of optional/nullable values.
 *
 * The Maybe pattern helps avoid null/undefined checks and provides a chainable API
 * for working with values that may or may not exist. It wraps values in two states:
 * - Just<T>: Contains a value
 * - Nothing<T>: Represents absence of a value
 *
 * Benefits:
 * - Eliminates null pointer exceptions
 * - Provides chainable operations (map, filter, find)
 * - Makes optional value handling explicit and safe
 * - Reduces nested if/null checks
 *
 * @example
 * // Safe property access without null checks
 * const productInfo = Maybe.of(sourceProductStore)
 *   .find(store => store.getProduct('HUB_APP'))
 *   .map(product => product.text)
 *   .unwrapOr('Default Product');
 *
 * @example
 * // Safe nested array/object navigation
 * const appName = Maybe.of(this._hubApps)
 *   .find(apps => apps.find(app => app.app_id === instanceId))
 *   .map(app => app.name)
 *   .unwrap(); // Returns string | undefined
 *
 * @example
 * // Chaining operations that might fail
 * const result = Maybe.of(user)
 *   .filter(u => u.isActive)
 *   .map(u => u.profile)
 *   .find(p => p.settings)
 *   .unwrapOr({ theme: 'default' });
 */
const Maybe = {
  /**
   * Creates a Maybe from a potentially null/undefined value.
   * This is the primary way to create a Maybe.
   *
   * @param value - The value to wrap (can be null or undefined)
   * @returns Just<T> if value exists, Nothing<T> if value is null/undefined
   *
   * @example
   * Maybe.of(user).map(u => u.name) // Safe even if user is null
   * Maybe.of(null).map(x => x.foo) // Returns Nothing, no error thrown
   */
  of<T>(value: T | null | undefined): IMaybe<T> {
    return value == null ? new Nothing<T>() : new Just(value);
  },

  /**
   * Creates a Maybe containing a known value (never null/undefined).
   * Use when you're certain the value exists.
   *
   * @param value - The value to wrap (must not be null/undefined)
   * @returns Just<T> containing the value
   *
   * @example
   * Maybe.just('hello').map(s => s.toUpperCase()) // Returns Just('HELLO')
   */
  just<T>(value: T): IMaybe<T> {
    return new Just(value);
  },

  /**
   * Creates a Maybe representing absence of a value.
   * Use as a default or placeholder for operations that found nothing.
   *
   * @returns Nothing<T> representing no value
   *
   * @example
   * const result = items.length > 0 ? Maybe.of(items[0]) : Maybe.nothing<Item>()
   */
  nothing<T>(): IMaybe<T> {
    return new Nothing<T>();
  },
};

export { Maybe };
